import { ApolloError } from '@apollo/client';
import { Box } from '@mui/material';

import {
  IPaginationProps,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  LoadedContent,
  Typography,
  TableContainer,
} from '@occc-app/design-system';
import { RetailerNetworkUserFragment, RetailerNetworkUserUpdate } from '@occc/graphql';

import { LocationUsersTableRow } from './location-users-table-row.component';

type LocationUsersTableProps = {
  data?: RetailerNetworkUserFragment[];
  loading: boolean;
  error?: ApolloError;
  isSearching?: boolean;
  isPhoneNumberVisible?: boolean;
  paginationProps: IPaginationProps;
  handleRedirectToUserDetailsPage: (userUuid: string) => void;
  handleRemoveUserFromLocation: (userUuid: string) => void;
  handleRemoveUserFromRetailerNetwork: (openCommerceUserUuid: string) => void;
  handleUpdateRetailerNetworkUserRole: (input: RetailerNetworkUserUpdate) => Promise<void>;
  isRetailerNetworkUserUpdateInProgress: boolean;
};

export const LocationUsersTable = ({
  data,
  loading,
  error,
  isSearching,
  isPhoneNumberVisible = false,
  paginationProps,
  handleRedirectToUserDetailsPage,
  handleRemoveUserFromLocation,
  handleRemoveUserFromRetailerNetwork,
  handleUpdateRetailerNetworkUserRole,
  isRetailerNetworkUserUpdateInProgress,
}: LocationUsersTableProps) => {
  const renderData = (items: RetailerNetworkUserFragment[]) => {
    const rows = items.map((item) => (
      <LocationUsersTableRow
        key={item.uuid}
        item={item}
        isPhoneNumberVisible={isPhoneNumberVisible}
        handleRedirectToUserDetailsPage={handleRedirectToUserDetailsPage}
        handleRemoveUserFromLocation={handleRemoveUserFromLocation}
        handleRemoveUserFromRetailerNetwork={handleRemoveUserFromRetailerNetwork}
        handleUpdateRetailerNetworkUserRole={handleUpdateRetailerNetworkUserRole}
        isRetailerNetworkUserUpdateInProgress={isRetailerNetworkUserUpdateInProgress}
      />
    ));

    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              {isPhoneNumberVisible && <TableCell>Mobile Phone Number</TableCell>}
              <TableCell>Role</TableCell>
              <TableCell align='right'>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{rows}</TableBody>
        </Table>
        <Pagination {...paginationProps} />
      </TableContainer>
    );
  };

  const renderNoData = () => (
    <Box
      py={14}
      maxWidth={500}
      margin='0 auto'
      display='flex'
      alignItems='center'
      flexDirection='column'
    >
      <Typography variant='h6' align='center' paragraph>
        {isSearching ? 'No Results Found' : 'Add or Create Network Users and Admins'}
      </Typography>
      <Typography align='center' color='textSecondary' paragraph>
        {isSearching
          ? 'Please adjust your query and try again.'
          : 'Network admins can edit all the network details, add new sites and new users. Network users have read-only access.'}
      </Typography>
    </Box>
  );

  return (
    <LoadedContent<RetailerNetworkUserFragment>
      error={error}
      loading={loading}
      items={data}
      renderData={renderData}
      renderNoData={renderNoData}
      renderEmpty={renderNoData}
    />
  );
};
